import { ChangeEvent, FormEvent, Fragment, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

import {
  createFoodBenefitImport,
  deleteFoodBenefitImportById,
  listFoodBenefitCodeAssignments,
  saveFoodBenefitCodeAssignments,
  saveFoodBenefitImportRecords,
} from '../services/foodBenefitImportsService';
import { parseFoodBenefitFiles } from '../services/foodBenefitParser';
import type { FoodBenefitAuditItem, FoodBenefitDistribution, FoodBenefitOrder, FoodBenefitParseTotals, FoodBenefitRecord } from '../services/foodBenefitTypes';
import { createEmptyFoodBenefitDistribution } from '../services/foodBenefitTypes';
import { isCompetenceConsolidated } from '../services/expensesConsolidationService';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { normalizeString } from '@/utils/stringUtils';

type CollaboratorReference = {
  id: string;
  name: string;
  cpf?: string | null;
  normalizedName: string;
  isActive: boolean;
};

type FoodBenefitMatchStatus = 'auto_code' | 'manual' | 'not_found';

type FoodBenefitMatch = {
  record: FoodBenefitRecord;
  collaboratorId: string | null;
  status: FoodBenefitMatchStatus;
};

const normalizeCodeKey = (code: string): string => code?.toString().trim().replace(/\s+/g, '') ?? '';

const formatCurrency = (value: number): string => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatCpf = (cpf?: string | null): string => {
  if (!cpf) return '';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const DISTRIBUTION_COLUMNS: Array<{ key: keyof FoodBenefitDistribution; label: string }> = [
  { key: 'meal', label: 'Refeição' },
  { key: 'food', label: 'Alimentação' },
  { key: 'mobility', label: 'Mobilidade' },
  { key: 'culture', label: 'Cultura' },
  { key: 'health', label: 'Saúde' },
  { key: 'education', label: 'Educação' },
  { key: 'homeOffice', label: 'Home office' },
  { key: 'freeBalance', label: 'Saldo livre' },
  { key: 'multi', label: 'Multi' },
];

const createEmptyTotals = (): FoodBenefitParseTotals => ({
  dependents: 0,
  feeCount: 0,
  amount: 0,
  breakdown: createEmptyFoodBenefitDistribution(),
});

function MappingCombobox({
  value,
  options,
  disabled,
  onChange,
}: {
  value?: string | null;
  options: CollaboratorReference[];
  disabled?: boolean;
  onChange: (id: string | null) => void;
}) {
  const [query, setQuery] = useState('');
  const selected = options.find((option) => option.id === value) ?? null;
  const deferredQuery = useDeferredValue(query);
  const filtered = useMemo(() => {
    const normalizedQuery = normalizeString(deferredQuery);
    if (!normalizedQuery) return options;
    return options.filter((option) => option.normalizedName.includes(normalizedQuery));
  }, [options, deferredQuery]);

  return (
    <Combobox value={selected} onChange={(option) => onChange(option?.id ?? null)} disabled={disabled}>
      {({ open }) => (
        <div className="relative w-full min-w-[16rem]">
          <Combobox.Input
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            displayValue={(option: CollaboratorReference | null) => option?.name ?? ''}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
            placeholder="Selecionar colaborador..."
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
          <Transition
            as={Fragment}
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filtered.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-500">Nenhum resultado</div>
              ) : (
                filtered.map((person) => (
                  <Combobox.Option
                    key={person.id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'
                      }`
                    }
                    value={person}
                  >
                    {({ selected: isSelected, active }) => (
                      <>
                        <span className="block">
                          <span className={`block ${isSelected ? 'font-semibold text-gray-900' : 'font-normal text-gray-900'}`}>
                            {person.name}
                          </span>
                          {person.cpf ? (
                            <span className="block text-xs text-gray-500">{formatCpf(person.cpf)}</span>
                          ) : null}
                        </span>
                        {isSelected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                              }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}

function DistributionList({ record }: { record: FoodBenefitRecord }) {
  const entries = DISTRIBUTION_COLUMNS.filter((column) => record[column.key] > 0);
  if (!entries.length) {
    return <span className="text-sm text-gray-500">Sem valores</span>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map((column) => (
        <span
          key={column.key}
          className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2 py-1 text-xs text-gray-700"
        >
          <span className="font-medium text-gray-900">{column.label}:</span>
          <span>{formatCurrency(record[column.key])}</span>
        </span>
      ))}
    </div>
  );
}

function StatusBadge({ status }: { status: FoodBenefitMatchStatus }) {
  const statusMap: Record<FoodBenefitMatchStatus, { text: string; className: string; Icon: typeof CheckCircleIcon }> = {
    auto_code: { text: 'Auto (CPF)', className: 'bg-green-100 text-green-700', Icon: CheckCircleIcon },
    manual: { text: 'Manual', className: 'bg-blue-100 text-blue-700', Icon: CheckCircleIcon },
    not_found: { text: 'Sem match', className: 'bg-amber-100 text-amber-700', Icon: ExclamationTriangleIcon },
  };
  const data = statusMap[status];
  const Icon = data.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${data.className}`}>
      <Icon className="h-4 w-4" aria-hidden />
      {data.text}
    </span>
  );
}

const FOOD_PROVIDER = {
  name: 'Caju',
  slug: 'caju',
};

export function FoodBenefitImportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success: showSuccess, error: showError } = useToast();
  const [billingPeriod, setBillingPeriod] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [matches, setMatches] = useState<FoodBenefitMatch[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [totals, setTotals] = useState<FoodBenefitParseTotals>(() => createEmptyTotals());
  const [orders, setOrders] = useState<FoodBenefitOrder[]>([]);
  const [codeAssignments, setCodeAssignments] = useState<Record<string, string>>({});
  const { data: collaborators, isLoading: loadingCollaborators } = useCollaboratorsRealtimeQuery();

  const collaboratorRefs = useMemo<CollaboratorReference[]>(() => {
    return (collaborators || []).map((collaborator) => {
      const name = collaborator.name ?? 'Sem nome';
      return {
        id: collaborator.id ?? '',
        name,
        cpf: (collaborator as any).cpf ?? null,
        normalizedName: normalizeString(name),
        isActive: (collaborator as any).status !== 'desligado' && (collaborator as any).status !== 'inactive',
      };
    });
  }, [collaborators]);

  const collaboratorByCpf = useMemo(() => {
    const map = new Map<string, CollaboratorReference>();
    collaboratorRefs.forEach((collab) => {
      const cpfDigits = (collab.cpf ?? '').replace(/\D/g, '');
      if (!cpfDigits || map.has(cpfDigits)) return;
      map.set(cpfDigits, collab);
    });
    return map;
  }, [collaboratorRefs]);

  const sourceFiles = useMemo(
    () =>
      files.map((file) => ({
        name: file.name,
        size: file.size,
        contentType: file.type || null,
      })),
    [files],
  );

  useEffect(() => {
    let active = true;
    const fetchAssignments = async () => {
      try {
        const data = await listFoodBenefitCodeAssignments();
        if (!active) return;
        setCodeAssignments(data);
      } catch (err) {
        console.error('[FoodBenefitImportPage] failed to load code assignments', err);
      }
    };
    void fetchAssignments();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!matches.length) return;
    setMatches((prev) => {
      let changed = false;
      const updated = prev.map((match) => {
        if (match.status !== 'not_found') return match;
        const normalizedCode = normalizeCodeKey(match.record.code);
        const assignedId = normalizedCode ? codeAssignments[normalizedCode] : undefined;
        if (!assignedId) return match;
        const collaborator = collaboratorRefs.find((ref) => ref.id === assignedId);
        if (!collaborator) return match;
        changed = true;
        return { ...match, collaboratorId: collaborator.id, status: 'auto_code' as FoodBenefitMatchStatus };
      });
      return changed ? updated : prev;
    });
  }, [codeAssignments, collaboratorRefs, matches]);

  useEffect(() => {
    if (!matches.length || collaboratorByCpf.size === 0) return;
    setMatches((prev) => {
      let changed = false;
      const updated = prev.map((match) => {
        if (match.collaboratorId) return match;
        const normalizedCode = normalizeCodeKey(match.record.code);
        if (!normalizedCode) return match;
        const collaborator = collaboratorByCpf.get(normalizedCode);
        if (!collaborator) return match;
        changed = true;
        return { ...match, collaboratorId: collaborator.id, status: 'auto_code' as FoodBenefitMatchStatus };
      });
      return changed ? updated : prev;
    });
  }, [collaboratorByCpf, matches]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files ? Array.from(event.target.files) : [];
    setFiles(fileList);
    setMatches([]);
    setTotals(createEmptyTotals());
    setOrders([]);
    setParseError(null);
  };

  const handleParse = async () => {
    if (!files.length) {
      setParseError('Selecione ao menos um arquivo de pedido exportado pela Caju para continuar.');
      return;
    }
    setParsing(true);
    setParseError(null);
    try {
      const result = await parseFoodBenefitFiles(files);
      const normalizedMatches: FoodBenefitMatch[] = result.records.map((record) => {
        const normalizedCode = normalizeCodeKey(record.code);
        const assignedCollaboratorId = normalizedCode ? codeAssignments[normalizedCode] : null;
        let collaborator: CollaboratorReference | undefined;
        let status: FoodBenefitMatchStatus = 'not_found';
        if (assignedCollaboratorId) {
          collaborator = collaboratorRefs.find((ref) => ref.id === assignedCollaboratorId);
          if (collaborator) {
            status = 'auto_code';
          }
        }
        if (!collaborator && normalizedCode) {
          const collaboratorByCpfMatch = collaboratorByCpf.get(normalizedCode);
          if (collaboratorByCpfMatch) {
            collaborator = collaboratorByCpfMatch;
            status = 'auto_code';
          }
        }
        return {
          record,
          collaboratorId: collaborator?.id ?? null,
          status,
        };
      });
      setMatches(normalizedMatches);
      setTotals(result.totals);
      setOrders(result.orders);
    } catch (error) {
      console.error('[FoodBenefitImportPage] parse error', error);
      setMatches([]);
      setTotals(createEmptyTotals());
      setOrders([]);
      setParseError('Não foi possível ler os arquivos. Confirme se são os pedidos oficiais da Caju.');
    } finally {
      setParsing(false);
    }
  };

  const handleMatchChange = (index: number, collaboratorId: string | null) => {
    setMatches((prev) =>
      prev.map((match, matchIndex) => {
        if (matchIndex !== index) return match;
        return {
          ...match,
          collaboratorId,
          status: collaboratorId ? 'manual' : 'not_found',
        };
      }),
    );
  };

  const handleImport = async () => {
    const competence = billingPeriod.trim();
    if (!competence) {
      showError('Informe a competência (AAAA-MM).');
      return;
    }
    const consolidated = await isCompetenceConsolidated(competence);
    if (consolidated) {
      showError('Não é possível importar: competência já consolidada.');
      return;
    }
    if (!matches.length) {
      showError('Realize o match dos colaboradores antes de salvar a importação.');
      return;
    }
    if (!user) {
      showError('É necessário estar autenticado para importar.');
      return;
    }
    const recordsPayload = matches.map((match) => ({
      ...match.record,
      collaboratorId: match.collaboratorId,
    }));

    setSubmitting(true);
    try {
      const audit: FoodBenefitAuditItem[] = matches
        .map((match): FoodBenefitAuditItem | null => {
          if (match.status === 'not_found') {
            return {
              beneficiaryName: match.record.name,
              beneficiaryCpf: match.record.code,
              value: match.record.amount,
              reason: 'Não vinculado a colaborador',
              type: 'not_found',
            };
          }
          const collaborator = collaboratorRefs.find((c) => c.id === match.collaboratorId);
          if (collaborator && !collaborator.isActive) {
            return {
              beneficiaryName: match.record.name,
              beneficiaryCpf: match.record.code,
              value: match.record.amount,
              reason: 'Colaborador inativo',
              type: 'inactive',
            };
          }
          return null;
        })
        .filter((item): item is FoodBenefitAuditItem => item !== null);

      const createdImport = await createFoodBenefitImport(
        FOOD_PROVIDER.slug,
        {
          billingPeriod: competence,
          totals,
          sourceFiles,
          recordsPreview: recordsPayload,
          orders,
          audit,
        },
        user,
      );

      try {
        await saveFoodBenefitImportRecords(createdImport.id, recordsPayload);
      } catch (recordsError) {
        console.error('[FoodBenefitImportPage] failed to persist records', recordsError);
        await deleteFoodBenefitImportById(createdImport.id);
        throw recordsError;
      }

      const assignmentsToSave = matches
        .filter((match) => match.collaboratorId)
        .map((match) => ({
          code: normalizeCodeKey(match.record.code),
          collaboratorId: match.collaboratorId!,
        }))
        .filter(
          (assignment) => assignment.code && codeAssignments[assignment.code] !== assignment.collaboratorId,
        );

      if (assignmentsToSave.length) {
        await saveFoodBenefitCodeAssignments(assignmentsToSave);
        setCodeAssignments((prev) => {
          const next = { ...prev };
          assignmentsToSave.forEach(({ code, collaboratorId }) => {
            next[code] = collaboratorId;
          });
          return next;
        });
      }
      showSuccess('Importação salva com sucesso.');
      navigate('/expenses/benefits/food');
    } catch (error) {
      console.error('[FoodBenefitImportPage] failed to save import', error);
      showError('Não foi possível salvar a importação.');
    } finally {
      setSubmitting(false);
    }
  };

  const sortedMatches = useMemo(() => {
    const priority: Record<FoodBenefitMatchStatus, number> = {
      not_found: 0,
      manual: 1,
      auto_code: 2,
    };
    return matches
      .map((match, originalIndex) => ({ match, originalIndex }))
      .sort((a, b) => {
        const diff = priority[a.match.status] - priority[b.match.status];
        if (diff !== 0) return diff;
        const nameA = a.match.record.name.toLowerCase();
        const nameB = b.match.record.name.toLowerCase();
        const nameDiff = nameA.localeCompare(nameB, 'pt-BR');
        if (nameDiff !== 0) return nameDiff;
        return a.originalIndex - b.originalIndex;
      });
  }, [matches]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="rounded-lg bg-primary-50 p-3 text-primary-700">
            <CreditCardIcon className="h-6 w-6" aria-hidden />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Benefícios</p>
            <h1 className="text-2xl font-semibold text-gray-900">Importar auxílio alimentação</h1>
            <p className="text-sm text-gray-600">
              Selecione a operadora e envie os arquivos de pedidos da competência desejada.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-md border border-primary-200 bg-white px-3 py-2 text-sm font-semibold text-primary-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden />
          Voltar
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Operadora</label>
            <select
              value={FOOD_PROVIDER.slug}
              disabled
              className="mt-1 w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed"
            >
              <option value={FOOD_PROVIDER.slug}>{FOOD_PROVIDER.name}</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Caju é a operadora inicial do benefício de alimentação.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Competência (AAAA-MM)</label>
            <input
              type="month"
              value={billingPeriod}
              onChange={(event) => setBillingPeriod(event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">Use o mês de referência presente nos arquivos da Caju.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Arquivos de pedido</label>
            <label className="mt-1 flex cursor-pointer items-center justify-between rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm transition hover:border-primary-400">
              <span className="inline-flex items-center gap-2">
                <CloudArrowUpIcon className="h-5 w-5 text-primary-600" aria-hidden />
                {files.length ? `${files.length} arquivo(s) selecionado(s)` : 'Selecionar arquivos (.csv)'}
              </span>
              <input type="file" accept=".csv" className="hidden" multiple onChange={handleFilesChange} />
            </label>
            <p className="mt-1 text-xs text-gray-500">Envie os arquivos de pedidos exportados da Caju.</p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleParse}
            disabled={!files.length || parsing}
            className="inline-flex items-center gap-2 rounded-md border border-primary-200 bg-white px-3 py-2 text-sm font-semibold text-primary-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {parsing ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Lendo arquivos...
              </>
            ) : (
              'Conferir'
            )}
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={submitting || parsing || matches.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Salvando
              </>
            ) : (
              <>
                Importar
                <ArrowRightIcon className="h-4 w-4" aria-hidden />
              </>
            )}
          </button>
        </div>
      </form>

      {parseError ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{parseError}</div>
      ) : null}

      {sortedMatches.length > 0 ? (
        <div className="space-y-3 rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Titulares (prévia)</h2>
              <p className="text-sm text-gray-600">
                Revise os pedidos importados, confira os valores distribuídos e associe cada registro a um colaborador.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span>Pedidos importados: {sortedMatches.length}</span>
              <span>Taxas registradas: {totals.feeCount}</span>
              <span>Valor total: {formatCurrency(totals.amount)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Distribuição dos valores</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {DISTRIBUTION_COLUMNS.map((column) => (
                <div key={column.key} className="rounded-md border border-gray-200 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{column.label}</p>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(totals.breakdown[column.key])}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Titular</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">CPF</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Valor total</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Saldos</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Match</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Colaborador</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {sortedMatches.map(({ match, originalIndex }) => (
                  <tr key={`${match.record.code}-${originalIndex}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-900">{match.record.name}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-sm text-gray-800 whitespace-nowrap">
                      {formatCpf(match.record.code)}
                    </td>
                    <td className="px-3 py-2 font-semibold text-gray-900">{formatCurrency(match.record.amount)}</td>
                    <td className="px-3 py-2">
                      <DistributionList record={match.record} />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={match.status} />
                    </td>
                    <td className="px-3 py-2">
                      <MappingCombobox
                        value={match.collaboratorId}
                        options={collaboratorRefs}
                        disabled={loadingCollaborators}
                        onChange={(id) => handleMatchChange(originalIndex, id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
